import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, XCircle, Clock, CheckCircle2, Target, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // 👈 1. Импорт хука
import api from '../api/api';

interface Trade {
  id: number;
  symbol: string;
  type: 'BUY' | 'SELL';
  price: number;
  amount: number;
  leverage: number;
  profit: number | null;
  profitUsdt?: number | null;
  score?: number;
  fundingRate?: number;
  isClosed: boolean;
  timestamp: string;
  closedAt: string | null;
  isPaper: boolean;
}

interface TradeHistoryProps {
  trades: Trade[];
  onRefresh: () => void;
  addLog: (msg: string) => void;
}

export const TradeHistoryTable: React.FC<TradeHistoryProps> = ({ trades, onRefresh, addLog }) => {
  const { t } = useTranslation(); // 👈 2. Инициализация хука
  const [closingId, setClosingId] = useState<number | null>(null);

  const handleClosePosition = async (trade: Trade) => {
    // 👇 Перевод подтверждения с параметром
    if (!window.confirm(t('platform.history.confirmClose', { symbol: trade.symbol }))) return;
    
    setClosingId(trade.id);
    try {
      await api.post('/trading/close-specific', { 
        symbol: trade.symbol, 
        tradeId: trade.id,
        amount: trade.amount 
      });
      // 👇 Перевод успеха
      addLog(t('platform.history.closeSuccess', { symbol: trade.symbol }));
      setTimeout(onRefresh, 1000);
    } catch (e: any) {
      alert(t('platform.history.error') + (e.response?.data?.message || e.message));
    } finally {
      setClosingId(null);
    }
  };

  return (
    <div className="bg-[#1e293b] border border-slate-800 rounded-[24px] overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900/80 text-left border-b border-slate-800">
              {/* 👇 Перевод заголовков */}
              <th className="p-5 text-xs font-black text-slate-500 uppercase tracking-widest">{t('platform.history.colAsset')}</th>
              <th className="p-5 text-xs font-black text-slate-500 uppercase tracking-widest">{t('platform.history.colDetails')}</th>
              <th className="p-5 text-xs font-black text-slate-500 uppercase tracking-widest">{t('platform.history.colVolume')}</th>
              <th className="p-5 text-xs font-black text-slate-500 uppercase tracking-widest">{t('platform.history.colPnl')}</th>
              <th className="p-5 text-xs font-black text-slate-500 uppercase tracking-widest text-right">{t('platform.history.colAction')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/40">
            {trades.length > 0 ? (
              trades.map((trade) => {
                const isProfit = (trade.profit || 0) >= 0;
                const notional = trade.amount * trade.price;
                
                return (
                  <tr key={trade.id} className="hover:bg-slate-800/40 transition-all group">
                    <td className="p-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xs ${
                          trade.isPaper ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}>
                          {trade.symbol.split('/')[0].substring(0, 3)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white tracking-tight">{trade.symbol}</span>
                            {trade.score && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 font-mono">
                                SC:{trade.score}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                            {new Date(trade.timestamp).toLocaleString([], { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                          </p>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-5">
                      <div className="flex flex-col gap-1">
                        <span className={`w-fit px-2 py-0.5 rounded text-[10px] font-black tracking-tighter ${
                          trade.type === 'BUY' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                        }`}>
                          {/* Типы BUY/SELL обычно оставляют как есть, но можно и перевести при желании */}
                          {trade.type} {trade.leverage > 1 ? `x${trade.leverage}` : ''}
                        </span>
                        <span className="font-mono text-slate-300 font-bold">
                          ${trade.price < 1 ? trade.price.toFixed(6) : trade.price.toLocaleString()}
                        </span>
                      </div>
                    </td>

                    <td className="p-5 font-mono text-slate-400 font-medium">
                      <div className="flex flex-col">
                        <span className="text-slate-200">${notional.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span className="text-[10px] opacity-50">{trade.amount.toFixed(4)} {t('platform.history.qty')}</span>
                      </div>
                    </td>

                    <td className="p-5">
                      {trade.isClosed ? (
                        <div className="flex flex-col">
                          <div className={`flex items-center gap-1 font-black text-base ${isProfit ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {isProfit ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
                            {trade.profit?.toFixed(2)}%
                          </div>
                          {trade.profitUsdt !== undefined && (
                            <span className={`text-xs font-bold ${isProfit ? 'text-emerald-500/70' : 'text-rose-500/70'}`}>
                              {isProfit ? '+' : ''}{trade.profitUsdt?.toFixed(2)} USDT
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-indigo-400 animate-pulse">
                          <Zap size={14} fill="currentColor" />
                          <span className="text-xs font-black uppercase tracking-widest text-indigo-300/80">{t('platform.history.trading')}</span>
                        </div>
                      )}
                    </td>

                    <td className="p-5 text-right">
                      {trade.isClosed ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700/50 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                          <CheckCircle2 size={12} className="text-slate-600" /> {t('platform.history.filled')}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleClosePosition(trade)}
                          disabled={closingId === trade.id}
                          className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500 hover:text-white border border-rose-500/20 transition-all duration-300 text-xs font-black uppercase tracking-tighter overflow-hidden shadow-lg shadow-rose-500/5"
                        >
                          {closingId === trade.id ? (
                            <div className="flex items-center gap-2">
                               <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                               {t('platform.history.closing')}
                            </div>
                          ) : (
                            <>
                              <XCircle size={14} className="group-hover:rotate-90 transition-transform duration-300" /> 
                              {t('platform.history.close')}
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="p-20 text-center">
                  <div className="flex flex-col items-center gap-4 opacity-20">
                    <div className="relative">
                      <Clock size={64} className="text-slate-400" />
                      <Target size={24} className="absolute -bottom-2 -right-2 text-slate-400" />
                    </div>
                    {/* 👇 Перевод пустого состояния */}
                    <p className="text-slate-400 font-black uppercase tracking-[0.2em] text-sm">{t('platform.history.emptyTitle')}</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <div className="bg-slate-900/30 p-4 border-t border-slate-800/50 flex justify-between items-center text-[10px] font-bold text-slate-600 uppercase tracking-widest">
        <div className="flex gap-4">
          <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-indigo-500" /> {t('platform.history.legendSim')}</span>
          <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" /> {t('platform.history.legendReal')}</span>
        </div>
        <span>{t('platform.history.totalOps')} {trades.length}</span>
      </div>
    </div>
  );
};